// libs
import React, { memo, useState, useMemo } from 'react';

// hooks
import { useKeyboardClick } from 'shared/hooks/useKeyboardClick';

// component
import Carousel from 'shared/components/Carousel';
import StarRating from 'shared/components/StarRating';

// constants
import { ARIA_LABELS } from './constants';

// context
import { useOverlaySheetContext } from 'shared/components/OverlaySheet';
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// helpers
import { getOptimisedImageUrl } from 'shared/helpers/utils';

// styles
import {
  Title,
  Details,
  Row,
  RatingRow,
  Marker,
  CheckMarker,
  BackIcon,
  ReviewWrapper,
  ZoomContainer,
  CarouselWrapper,
  Clicker,
} from './styles';

// constants
import { VERIFIED_USER } from './constants';
import { COLORS } from 'shared/styles/base';

// icons
import ChevronLeft from '@nykaa/ui-components/Icons/chevron-left';

const DEFAULT_IMG_TITLE = 'customer review image';

interface IImageGalleryProps {
  activeIndex: number;
  reviews: { [key: string]: Review.IReviewData; };
  images: ReviewAllImages.IImageItem[];
}

function ImageGallery({ reviews, images, activeIndex = 0 }: IImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(activeIndex);
  const [showDetails, setShowDetails] = useState(false);

  const { doHide } = useOverlaySheetContext();
  const { deviceWidth } = useResponsiveContext();

  const { title, description, rating, name, isBuyer, reviewCreationText } = useMemo(() => {
    const selectedImageId = images[selectedImage].id;

    // return review data on the basis of selected Image Id
    return reviews[selectedImageId];
  }, [images, reviews, selectedImage]);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const closeDetails = () => {
    setShowDetails(false);
  };

  const closeKeyboardProps = useKeyboardClick(doHide);
  const toggleKeyboardProps = useKeyboardClick(toggleDetails);

  const showNav = images.length > 1;

  return (
    <ZoomContainer data-test="ZoomContainer">
      <BackIcon
        {...closeKeyboardProps}
        aria-label={ARIA_LABELS.CLOSE_IMAGE_GALLERY}
      >
        <ChevronLeft />
      </BackIcon>

      <CarouselWrapper>
        <Carousel
          slideWidth={deviceWidth}
          currentSlide={selectedImage}
          onChange={setSelectedImage}
          showNav={showNav}
          navPadding={0}
        >
          {images.map((item, indx) => (
            <button className="image-wrapper" onClick={closeDetails} key={"image-wrapper" + indx}>
              <img
                loading="lazy"
                src={getOptimisedImageUrl(item.url, { width: deviceWidth * 2 })}
                width={deviceWidth}
                alt={DEFAULT_IMG_TITLE}
              />
            </button>
          ))}
        </Carousel>
      </CarouselWrapper>

      <ReviewWrapper
        {...toggleKeyboardProps}
        showDetails={showDetails}
        aria-label={showDetails ? ARIA_LABELS.HIDE_REVIEW_DETAILS : ARIA_LABELS.SHOW_REVIEW_DETAILS}
        aria-expanded={showDetails}
      >
        <Clicker />
        <RatingRow>
          <StarRating
            className="star-rating"
            size="16px"
            activeCount={rating}
            activeColor={COLORS.BLACK_92}
          />
          {reviewCreationText && <span>{reviewCreationText}</span>}
        </RatingRow>

        <Title>{title}</Title>

        <Details>{description}</Details>

        <Row>
          <span className="name">{name}</span>
          {isBuyer && (
            <Marker>
              <CheckMarker />
              <span className="verified">{VERIFIED_USER}</span>
            </Marker>
          )}
        </Row>
      </ReviewWrapper>
    </ZoomContainer>
  );
}

export default memo(ImageGallery);
